import React, { useState } from "react";
import { toast } from "sonner";
import { PlugZap, CheckCircle2, XCircle, RefreshCw, Loader2, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import { GRAPH_PERMISSIONS } from "./TenantFormDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "never");

export default function ConnectionPanel({ tenant, isAdmin, onChange }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(tenant.status !== "connected");
  const perms = tenant.permissions?.permissions?.length ? tenant.permissions.permissions : GRAPH_PERMISSIONS.map((p) => ({ ...p, granted: null }));
  const granted = perms.filter((p) => p.granted).length;
  const missingCritical = perms.filter((p) => p.critical && p.granted === false);

  const verify = async () => {
    setBusy(true);
    try {
      const res = await authApi.tenants.verify(tenant.id);
      if (res.ok) toast.success("Connection OK", { description: `${res.permissions?.permissions?.filter((p) => p.granted).length || 0} of ${perms.length} permissions granted` });
      else toast.error("Connection failed", { description: res.error });
      onChange?.();
    } catch (err) {
      toast.error("Could not test the connection", { description: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ws-card glass connection" data-testid="connection-panel">
      <div className="ws-card-head">
        <h2>
          <PlugZap size={16} /> Graph connection
          <span className={`status-pill ${tenant.status === "connected" ? "ok" : tenant.status === "error" ? "warn" : ""}`} data-testid="connection-status">
            {tenant.status === "connected" ? "Connected" : tenant.status === "error" ? "Needs attention" : "Not verified"}
          </span>
          {tenant.permissions?.permissions?.length ? (
            <span className="muted small" data-testid="connection-perms-count">
              {granted}/{perms.length} permissions · verified {fmt(tenant.verified_at)}
            </span>
          ) : null}
        </h2>
        <div className="tenant-actions">
          {isAdmin && (
            <button type="button" className="tb-btn sm" disabled={busy} onClick={verify} data-testid="connection-test">
              {busy ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />} Test connection
            </button>
          )}
          <button type="button" className="tb-btn sm ghost" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="connection-toggle">
            {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {open ? "Hide" : "Details"}
          </button>
        </div>
      </div>
      {tenant.status === "error" && tenant.last_error && (
        <div className="ws-alert warn" data-testid="connection-error">
          <AlertTriangle size={13} /> {tenant.last_error}
        </div>
      )}
      {open && (
        <>
          <dl className="detail-grid compact">
            <div>
              <dt>Directory (tenant) ID</dt>
              <dd className="mono">{tenant.tenant_id}</dd>
            </div>
            <div>
              <dt>Application (client) ID</dt>
              <dd className="mono">{tenant.client_id_masked || tenant.client_id}</dd>
            </div>
            <div>
              <dt>Client secret</dt>
              <dd>{tenant.has_secret ? "stored (encrypted)" : "missing"}</dd>
            </div>
            <div>
              <dt>Last scan</dt>
              <dd>{fmt(tenant.last_scan_at)}</dd>
            </div>
          </dl>
          <ul className="perm-list" data-testid="permission-list">
            {perms.map((p) => (
              <li key={p.role} className={p.granted ? "ok" : p.granted === false ? (p.critical ? "bad" : "missing") : ""} data-testid={`perm-${p.role}`}>
                {p.granted ? <CheckCircle2 size={14} /> : p.granted === false ? <XCircle size={14} /> : <span className="dot" />}
                <span className="mono">{p.role}</span>
                <span className="muted">{p.why}</span>
                {p.critical && <span className="mini-chip warn">required</span>}
              </li>
            ))}
          </ul>
          {missingCritical.length > 0 && (
            <div className="ws-alert warn">
              <AlertTriangle size={13} /> Add {missingCritical.map((p) => p.role).join(", ")} as <b>application</b> permissions in Entra, grant admin consent, then test again.
            </div>
          )}
        </>
      )}
    </section>
  );
}
